import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

type useFAQViewedEventProps = {
	spaceKey: string;
	questionCount: number;
};

// Fires a single "module viewed" analytics event per page load when the FAQ
// section scrolls into view. The event is intentionally fire-once — it tracks
// whether the user saw the module at all.
export const useFAQViewedEvent = ({ spaceKey, questionCount }: useFAQViewedEventProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const sectionRef = useRef<HTMLElement>(null);
	const hasFiredRef = useRef(false);

	// questionCount may change when refetching data, but we only need the latest value
	// when the intersection fires — not as a trigger to re-observe.
	const questionCountRef = useRef(questionCount);
	questionCountRef.current = questionCount;

	useEffect(() => {
		const node = sectionRef.current;
		if (!node) {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !hasFiredRef.current) {
					hasFiredRef.current = true;
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'viewed',
							actionSubject: 'module',
							actionSubjectId: 'faq',
							source: 'smartSpaceOverview',
							attributes: {
								moduleType: 'faq',
								spaceKey,
								questionCount: questionCountRef.current,
							},
						},
					}).fire();
					observer.disconnect();
				}
			},
			{ threshold: 0.5 },
		);

		observer.observe(node);

		return () => {
			observer.disconnect();
		};
	}, [spaceKey, createAnalyticsEvent]);

	return { sectionRef };
};
